import { ImageOffIcon } from "lucide-react";

import { formatUSDShort } from "@/web/lib/formatting";
import {
  resolvePlacePhotoUrlByName,
  usePlacePhotoUrlByPlaceId,
} from "@/web/lib/googlePlaces/photoUtils";
import { cn } from "@/web/lib/styling";
import type { ExplorePin } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/types";

function attributeTags(pin: ExplorePin): string[] {
  const tags: string[] = [];
  if (pin.lastSalePrice != null) tags.push(`${formatUSDShort(pin.lastSalePrice)} Last sale`);
  else if (pin.askingPrice != null) tags.push(`${formatUSDShort(pin.askingPrice)} Asking`);
  if (pin.sqft != null) tags.push(`${pin.sqft.toLocaleString()} SqFt`);
  return tags;
}

/**
 * Image-backed property card with an Add/Remove affordance. Used both as the
 * single full-width card shown when a pin is tapped (`variant="full"`) and as a
 * fixed-width item in the default horizontal scroller (`variant="compact"`).
 */
export function ExplorePropertyCard({
  pin,
  selected,
  disabled,
  onToggle,
  variant = "full",
}: {
  pin: ExplorePin;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
  variant?: "full" | "compact";
}) {
  const tags = attributeTags(pin);
  const { address } = pin;
  const namedPhotoUrl = resolvePlacePhotoUrlByName(pin.googlePlacePhotoName, 480);
  const { url: placeIdPhotoUrl, isPending: placePhotoPending } = usePlacePhotoUrlByPlaceId(
    (pin.imageUrl ?? namedPhotoUrl) ? undefined : pin.googlePlaceId,
    480,
  );
  const photoUrl = pin.imageUrl ?? namedPhotoUrl ?? placeIdPhotoUrl;
  const photoPending = !photoUrl && placePhotoPending;

  return (
    <div
      className={cn(
        "relative overflow-hidden bg-withus-dark-blue",
        variant === "full" ? "h-[220px] w-full" : "h-[180px] w-[260px] shrink-0",
      )}
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : photoPending ? (
        <div className="absolute inset-0 bg-muted" aria-hidden />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground"
          aria-hidden
        >
          <ImageOffIcon className="size-10" />
        </div>
      )}
      {/* Scrim for text legibility — applied regardless of image presence so the
          white address/tags stay readable over photos AND the light no-image
          fallback. Darkens toward the top (address) and bottom (button). */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-withus-pure-black/60 via-withus-pure-black/15 to-withus-pure-black/60"
        aria-hidden
      />

      <div className="relative z-10 flex h-full flex-col justify-between p-5">
        <div className="flex flex-col gap-3">
          <div className="font-heading text-base leading-[1.1] text-withus-white uppercase">
            <p>{address.addressLine1}</p>
            <p>{[address.city, address.state, address.zip].filter(Boolean).join(" ")}</p>
          </div>
          {variant === "full" && tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-withus-white/25 px-2 py-1.5 text-[11px] font-semibold tracking-wide text-withus-white uppercase"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div>
          <button
            type="button"
            disabled={disabled}
            onClick={onToggle}
            className={cn(
              "px-3 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
              selected ? "bg-withus-red text-withus-white" : "bg-withus-white text-withus-red",
            )}
          >
            {selected ? "Remove" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
